import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { FileSpreadsheet } from 'lucide-react';

export const ReimportExcelDialog = ({
  open,
  onOpenChange,
  reimportFile,
  setReimportFile,
  reimportResult,
  reimportLoading,
  handleReimportExcel,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl" data-testid="reimport-dialog">
      <DialogHeader>
        <DialogTitle>Re-import &amp; Replay Allocations</DialogTitle>
        <DialogDescription>
          Upload <strong>either</strong> the latest loanbook <span className="font-mono">.xlsb/.xlsx</span>
          <strong> OR</strong> the investor-transactions <span className="font-mono">.csv</span>. The system
          auto-detects the format and wipes / re-imports only the matching dataset, then re-binds
          any allocations that pointed at the old rows. Bank/Investec allocations are <strong>NOT</strong> touched.
        </DialogDescription>
      </DialogHeader>
      {!reimportResult ? (
        <div className="space-y-4 py-2">
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
            <FileSpreadsheet className="h-8 w-8 mx-auto text-gray-400 mb-2" />
            <Input
              type="file"
              accept=".xlsx,.xls,.xlsb,.csv"
              onChange={e => setReimportFile(e.target.files?.[0] || null)}
              className="max-w-xs mx-auto"
              data-testid="reimport-file-input"
            />
            {reimportFile && <p className="text-sm text-gray-600 mt-2">{reimportFile.name}</p>}
          </div>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
            <p className="font-semibold mb-1">Excel (.xlsb/.xlsx) mode:</p>
            <ul className="list-disc list-inside space-y-0.5 mb-2">
              <li>Wipe and re-flatten <span className="font-mono">expected_cashflows</span> (RP &amp; LB sheets)</li>
              <li>Refresh loan book, risk participation, and interest / running balances</li>
              <li>Smart-rebind cashflow allocations within ±2 days / ±R0.01 tolerance</li>
            </ul>
            <p className="font-semibold mb-1">CSV mode:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Rows already matched/verified are matched by (investor, date, loan) — a corrected amount/type/reference updates that row in place, keeping its match/verify status</li>
              <li>Rows with no reconciled counterpart in the file are wiped and re-inserted as before</li>
              <li>Re-bind allocations by exact (RP code, date, amount, type) match for any row that was actually removed</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={reimportLoading}>Cancel</Button>
            <Button
              onClick={handleReimportExcel}
              disabled={!reimportFile || reimportLoading}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              data-testid="reimport-confirm-btn"
            >
              {reimportLoading ? 'Importing… (30-60s)' : 'Upload & Replay'}
            </Button>
          </DialogFooter>
        </div>
      ) : (
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto" data-testid="reimport-result">
          {reimportResult.status === 'failed' ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              <p className="font-semibold">Import failed</p>
              <p className="font-mono text-xs mt-1 break-all">{reimportResult.error}</p>
            </div>
          ) : (
            <>
              <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-900">
                <p className="font-semibold">
                  {reimportResult.mode === 'csv' ? 'CSV re-import & replay complete' : (reimportResult.message || 'Re-import complete')}
                </p>
                <p className="text-xs mt-1">Duration: {reimportResult.duration_seconds}s · Import ID: <span className="font-mono">{reimportResult.import_id?.slice(0,8)}...</span></p>
              </div>
              {reimportResult.mode === 'csv' ? (
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                    <p className="text-2xl font-bold text-emerald-700">{reimportResult.csv_summary?.preserved_match_count ?? 0}</p>
                    <p className="text-xs text-emerald-600">Corrected in place — match kept</p>
                  </div>
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                    <p className="text-2xl font-bold text-orange-700">{reimportResult.csv_summary?.old_csv_rows_deleted ?? 0}</p>
                    <p className="text-xs text-orange-600">Old rows removed (gone from file)</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-2xl font-bold text-blue-700">{reimportResult.csv_summary?.new_csv_rows_imported ?? 0}</p>
                    <p className="text-xs text-blue-600">New CSV rows imported</p>
                  </div>
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded">
                    <p className="text-2xl font-bold text-rose-700">{(reimportResult.csv_summary?.errors || []).length}</p>
                    <p className="text-xs text-rose-600">Row errors</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-2xl font-bold text-blue-700">{reimportResult.cashflow_diff?.added_count ?? 0}</p>
                    <p className="text-xs text-blue-600">Cashflows added</p>
                  </div>
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                    <p className="text-2xl font-bold text-orange-700">{reimportResult.cashflow_diff?.removed_count ?? 0}</p>
                    <p className="text-xs text-orange-600">Cashflows removed</p>
                  </div>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded">
                    <p className="text-2xl font-bold text-gray-700">{reimportResult.cashflow_diff?.unchanged_count ?? 0}</p>
                    <p className="text-xs text-gray-600">Unchanged</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-center text-sm">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                  <p className="text-2xl font-bold text-emerald-700">{reimportResult.allocations_replay?.rebound_count ?? 0}</p>
                  <p className="text-xs text-emerald-600">Allocations re-bound</p>
                </div>
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-2xl font-bold text-red-700">{reimportResult.allocations_replay?.orphaned_count ?? 0}</p>
                  <p className="text-xs text-red-600">Orphaned — need review</p>
                </div>
              </div>
              {(reimportResult.allocations_replay?.orphaned || []).length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-xs">
                  <p className="font-semibold text-red-800 mb-1">Orphaned allocations (manual review required):</p>
                  <ul className="space-y-0.5 font-mono">
                    {reimportResult.allocations_replay.orphaned.slice(0, 10).map((o, i) => (
                      <li key={i} className="text-red-700 truncate">
                        {o.source_sheet ? `${o.source_sheet}/${o.loan_id}` : `${o.rp_code} ${o.type}`} · {o.date} · R{Number(o.amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {reimportResult.mode !== 'csv' && (reimportResult.allocations_replay?.rebound || []).length > 0 && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs">
                  <p className="font-semibold text-emerald-800 mb-1">Re-bound allocations (top 10):</p>
                  <ul className="space-y-0.5 font-mono">
                    {reimportResult.allocations_replay.rebound.slice(0, 10).map((r, i) => (
                      <li key={i} className="text-emerald-700 truncate">
                        Δdays={r.delta_days}, ΔR{r.delta_amount} · {r.old_key} → {r.new_key}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {reimportResult.mode === 'csv' && (reimportResult.csv_summary?.errors || []).length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  <p className="font-semibold text-yellow-800 mb-1">CSV row errors:</p>
                  <ul className="space-y-0.5">
                    {reimportResult.csv_summary.errors.slice(0, 10).map((e, i) => (
                      <li key={i} className="text-yellow-800">Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(reimportResult.loanbook_summary?.errors || []).length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-xs">
                  <p className="font-semibold text-yellow-800 mb-1">Loanbook import warnings:</p>
                  <ul className="space-y-0.5">
                    {reimportResult.loanbook_summary.errors.slice(0, 10).map((e, i) => (
                      <li key={i} className="text-yellow-700">{String(e)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
